import { useEffect, useState } from "react";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { useSession } from "../../lib/auth-client";
import { fmtDate, fmtRelative } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import { listAdmins, inviteAdmin, revokeAdmin } from "../../lib/adminApi";

function generatePassword() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  const buf = new Uint32Array(16);
  crypto.getRandomValues(buf);
  for (const n of buf) out += alphabet[n % alphabet.length];
  return out;
}

function AdminsInner() {
  const { toast, openModal, closeModal } = useApp();
  const { data: session } = useSession();
  const meId = session?.user?.id;
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setAdmins(await listAdmins());
    } catch (e) {
      toast(e.message || "Failed to load admins", "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onInvite = () => {
    const data = { email: "", password: generatePassword(), name: "", phone: "" };
    const submit = async () => {
      if (!data.email || !data.email.includes("@")) return toast("Valid email required", "error");
      if (!data.password || data.password.length < 8) return toast("Password ≥ 8 chars required", "error");
      try {
        await inviteAdmin(data);
        toast(`Admin invited. Temp password: ${data.password}`, "success");
        closeModal();
        load();
      } catch (e) { toast(e.message, "error"); }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Invite platform admin</h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Email</label>
            <input className="input" type="email" onChange={(e) => (data.email = e.target.value)} />
          </div>
          <div className="field">
            <label>Name</label>
            <input className="input" onChange={(e) => (data.name = e.target.value)} />
          </div>
          <div className="field">
            <label>Phone (E.164, optional)</label>
            <input className="input" placeholder="+91…" onChange={(e) => (data.phone = e.target.value)} />
          </div>
          <div className="field">
            <label>Temp password (auto-generated)</label>
            <input
              className="input"
              type="text"
              defaultValue={data.password}
              onChange={(e) => (data.password = e.target.value)}
            />
            <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              Share this with the new admin securely. They will be prompted to change it after first sign in.
            </small>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>
            <Icon name="user-plus" size={14} /> Invite
          </button>
        </div>
      </>,
    );
  };

  const onRevoke = (a) => {
    const otherAdminCount = admins.filter((x) => x.id !== a.id).length;
    openModal(
      <>
        <div className="modal-head">
          <h3 style={{ color: "var(--error)" }}>
            <Icon name="shield-off" size={16} /> Revoke admin
          </h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Revoke platform access for <strong>{a.email}</strong>?
            Their sessions will be killed and role downgraded.
          </p>
          {otherAdminCount === 0 && (
            <p style={{ fontSize: 13, color: "var(--error)" }}>
              You'd be the only remaining admin afterwards. Cannot proceed.
            </p>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button
            className="btn"
            style={{ background: "var(--error)", color: "#fff" }}
            disabled={otherAdminCount === 0}
            onClick={async () => {
              try { await revokeAdmin(a.id); toast("Revoked", "success"); closeModal(); load(); }
              catch (e) { toast(e.message, "error"); }
            }}
          >
            <Icon name="shield-off" size={14} /> Revoke
          </button>
        </div>
      </>,
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Platform Admins</h1>
          <p className="page-sub">{admins.length} admin{admins.length === 1 ? "" : "s"} on the platform</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={onInvite}>
          <Icon name="user-plus" size={14} /> Invite Admin
        </button>
      </div>

      {loading ? (
        <TableSkeleton columns={4} rows={3} />
      ) : admins.length === 0 ? (
        <EmptyState title="No admins" desc="Invite the first platform admin." icon="shield" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Admin</th>
                <th>Created</th>
                <th>Last Session</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const last = a.sessions?.[0]?.createdAt;
                const self = a.id === meId;
                return (
                  <tr key={a.id}>
                    <td data-label="Admin">
                      <div className="cell-emp">
                        <Avatar name={a.name || a.email} size="sm" seed={(a.email || "").length} />
                        <div>
                          <div className="name">
                            {a.name || "—"}
                            {self && (
                              <span className="badge badge-info" style={{ marginLeft: 8 }}>You</span>
                            )}
                          </div>
                          <div className="sub">{a.email}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Created" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {fmtDate(a.createdAt)}
                    </td>
                    <td data-label="Last Session" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {last ? fmtRelative(last) : "never"}
                    </td>
                    <td data-label="">
                      <div className="row-actions">
                        <button
                          className="icon-btn"
                          title={self ? "Cannot revoke yourself" : "Revoke admin"}
                          disabled={self}
                          style={{ color: self ? "var(--text-muted)" : "var(--error)" }}
                          onClick={() => !self && onRevoke(a)}
                        >
                          <Icon name="shield-off" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminAdmins() {
  return (
    <AdminPageBoundary>
      <AdminsInner />
    </AdminPageBoundary>
  );
}
